<template>
  <div class="lesson-comment p-3" :class="{ child }">
    <div class="d-flex align-items-center mb-2 comment-header">
      <img v-if="userPhoto" :src="userPhoto" class="user-img" />
      <div v-else class="user-img">
        <span class="letter">{{ name[0] }}</span>
      </div>
      <span class="user-name">
        {{ name }}
      </span>
      <small class="comment-date">{{ created_at }}</small>
      <div
        v-if="!(this.comment.status === 'published')"
        :class="`tag-status text-truncate ${comment.status}`"
      >
        {{ status }}
      </div>
    </div>
    <b-dropdown
      v-if="$user.user && comment.user_id === $user.user.id"
      variant="link"
      toggle-class="text-decoration-none"
      right
    >
      <template #button-content>
        <i class="icon-options text-secondary"></i>
      </template>
      <b-dropdown-item
        v-if="this.comment.status === 'review'"
        @click="editing = true"
        link-class="d-item"
      >
        <i class="icon-edit-filled"></i> Editar comentário
      </b-dropdown-item>
      <b-dropdown-item @click="removing = true" link-class="d-item">
        <i class="icon-trash"></i> Apagar comentário
      </b-dropdown-item>
    </b-dropdown>

    <div class="mb-4 comment-text">
      <span>{{ comment.text }}</span>
    </div>

    <div class="d-flex w-100 justify-content-end align-items-center">
      <span class="btn-interaction mr-2">
        {{ comment.likes }}
      </span>
      <button class="btn-interaction" @click="like">
        <i :class="comment.liked ? 'icon-liked' : 'icon-like'" />
      </button>
      <div v-if="!child">
        <div class="btn-interaction">
          {{ childComments.length }}
          <i class="icon-talk ml-2"></i>
        </div>
        <button class="btn-interaction" @click="answering = true">
          Comentar
        </button>
      </div>
    </div>

    <div v-for="(child, i) in childComments" :key="child.id" class="pl-4">
      <div
        class="mt-4"
        :class="{
          'divider-border-bottom pb-2':
            childComments.length > 1 && i < childComments.length - 1
        }"
      >
        <lesson-comment
          :comment="child"
          child
          @success-noreload="$emit('success-noreload')"
        />
      </div>
    </div>

    <send-comment
      v-if="answering"
      :lesson="$route.params.lesson"
      :parent="comment.id"
      @cancel="answering = false"
      @success="$emit('success')"
      @success-noreload="$emit('success-noreload')"
    />

    <b-modal
      v-model="editing"
      hide-header
      hide-footer
      centered
      size="md"
      :modal-class="`theme-${$setup.theme}-modals`"
    >
      <div class="edit-comment-modal">
        <button class="btn icon-close" type="button" @click="editing = false" />
        <form @submit.prevent="edit">
          <h5 class="page-title text-center mb-4">Editar comentário</h5>
          <form-group
            placeholder="Comentar..."
            id="text"
            v-model="form.text"
            :errors="errors.text"
            textarea
          />
          <div class="d-flex align-items-center justify-content-end pt-4">
            <button
              type="button"
              class="btn btn-outline-secondary mr-4"
              @click="editing = false"
            >
              Cancelar
            </button>
            <button type="submit" class="btn btn-primary">
              <loading :show="loading" small>Salvar</loading>
            </button>
          </div>
        </form>
      </div>
    </b-modal>

    <b-modal
      v-model="removing"
      hide-header
      hide-footer
      centered
      :modal-class="`theme-${$setup.theme}-modals`"
    >
      <div class="remove-comment-confirmation-modal">
        <button class="btn icon-close" type="button" @click="editing = false" />
        <h5 class="page-title text-center mb-5">
          Tem certeza que deseja excluir?
        </h5>
        <div class="d-flex align-items-center justify-content-center">
          <button
            class="btn btn-outline-secondary mr-4"
            @click="removing = false"
          >
            Cancelar
          </button>
          <button class="btn btn-primary" @click="remove">
            <loading :show="loading">Sim, excluir</loading>
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { formFields, timeFromNow } from "@/functions/helpers";
import SendComment from "./SendComment";

export default {
  name: "lesson-comment",
  components: { SendComment },
  props: {
    comment: {
      type: Object,
      default: () => ({})
    },
    child: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      answering: false,
      editing: false,
      removing: false,
      loading: false,

      ...formFields(["text"], { ...this.comment })
    };
  },
  computed: {
    name() {
      if (this.comment.is_producer && this.comment.moderator_data?.name)
        return this.comment.moderator_data?.name;

      if (this.comment.name && this.comment.name.length)
        return this.comment.name;

      return "Usuário sem nome";
    },
    status() {
      const STATUS_MAP = {
        review: "Em revisão",
        published: "Publicado",
        blocked: "Bloqueado"
      };
      return STATUS_MAP[this.comment.status];
    },
    created_at() {
      return timeFromNow(this.comment.created_at).replace(/\b(há|\.)/g, "");
    },
    userPhoto() {
      return this.comment.is_producer
        ? this.comment.moderator_data?.photo
        : this.comment.user_photo;
    },
    childComments() {
      let parameter = this.comment.children ? "children" : "childers"; //fallback
      return this.comment[parameter]
        .filter(
          c =>
            !(this.$user.user.id !== c.user_id && c.status === "review") &&
            c.status !== "blocked"
        )
        .reverse();
    }
  },
  methods: {
    like() {
      this.comment.liked = !this.comment.liked;
      this.comment.like = this.comment.liked
        ? this.comment.like + 1
        : this.comment.like - 1;
      this.$store
        .dispatch("forum/like", this.comment.id)
        .then(() => {
          this.$emit("success-noreload");
        })
        .catch(() => {
          this.comment.liked = !this.comment.liked;
        });
    },
    remove() {
      this.loading = true;
      this.$store
        .dispatch("forum/remove", this.comment.id)
        .then(() => {
          this.$message.success("Comentário excluído com sucesso");
          this.removing = false;
          this.$emit("success");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    edit() {
      this.loading = true;
      this.$store
        .dispatch("forum/edit", {
          ...this.comment,
          ...this.form
        })
        .then(() => {
          this.$message.success("Comentário editado com sucesso");
          this.editing = false;
          this.$emit("success");
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.lesson-comment {
  position: relative;
  border-radius: 8px;

  .comment-header {
    display: flex;
    align-items: center;
    gap: 12px;
    .user-img {
      height: 40px;
      width: 40px;
      object-fit: cover;
      border-radius: 50%;
      background: rgba(32, 32, 32, 0.9);

      display: flex;
      align-items: center;
      justify-content: center;

      .letter {
        color: var(--verso-dark-grey-1, #bebfc7);
        text-align: center;
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: 100%;
      }
    }
    .comment-date {
      font-size: 12px;
    }

    .user-name {
      font-weight: 500;
      font-size: 14px;

      // @media screen and (max-width: 525px) {
      //   width: 250px;
      // }
      // @media screen and (max-width: 490px) {
      //   width: 200px;
      // }
      // @media screen and (max-width: 450px) {
      //   width: 160px;
      // }
      // @media screen and (max-width: 400px) {
      //   width: 120px;
      // }
      // @media screen and (max-width: 379px) {
      //   width: 80px;
      // }
      // @media screen and (max-width: 350px) {
      //   width: 60px;
      // }
    }
  }

  .comment-text {
    overflow-wrap: anywhere;
    padding-right: 12px;
    max-height: 120px;
    overflow-y: scroll;
    &::-webkit-scrollbar {
      width: 6px !important;
    }
    &::-webkit-scrollbar-thumb {
      background: #d0d1ddd9;
      border-radius: 10px;
    }
  }

  .tag-status {
    border-radius: 4px;
    text-align: center;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 20px;
    padding: 0 4px;

    &.review {
      background: rgba(171, 133, 229, 0.2);
      color: var(--verso-dark-roxo-claro, #ab85e5);
    }

    &.published {
      background: rgba(45, 143, 80, 0.2);
      color: rgba(45, 143, 80, 1);
    }
  }

  .btn-interaction {
    border: none;
    background: transparent;
    padding: 0;
    display: inline-flex;
    align-items: center;

    i {
      font-size: 24px;
      margin-right: 8px;
    }

    img {
      max-height: 20px;
      margin-right: 8px;
    }
  }

  hr {
    border-color: black;
  }

  ::v-deep .dropdown {
    position: absolute;
    top: 2px;
    right: -21px;
  }

  ::v-deep .dropdown-menu {
    box-shadow: none !important;
    border: none !important;
    background: #fff !important;
  }

  ::v-deep .dropdown-toggle {
    font-size: 24px;
    padding: 0px;

    &::after {
      display: none;
    }
  }

  ::v-deep .dropdown-item {
    font-weight: 400 !important;
    font-size: 14px !important;
    i {
      color: #000 !important;
    }
  }

  .count {
    color: #d7d7d7;
    display: inline-flex;
    align-items: center;

    i {
      font-size: 24px;
    }
  }
}
</style>
